import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { Subject, takeUntil } from 'rxjs';

import { JobPcvizService } from '@mirage-projects/cw-pcviz-core';

import { UploadWizardService } from '../../../services/upload-wizard-service/upload-wizard.service';
import { ObjectBrowserSelectionService } from '../../object-browser/selection.service';
import { SelectExistingLayerService } from '../../select-existing-layer/select-existing-layer.service';

type LayerSelectionMode = 'none' | 'new' | 'existing';

/**
 * This component let user select from the different possibilites when creating a layer
 * - Existing layer
 * - New Layer
 */
@Component({
    selector: 'cw-select-layer',
    templateUrl: 'select-layer.component.html',
    styleUrls: ['select-layer.component.scss'],
    standalone: false,
})
export class SelectLayerComponent implements OnInit, OnDestroy {
    selection: LayerSelectionMode = 'none';
    onDestroy$: Subject<void> = new Subject();

    constructor(
        public uploadWizardService: UploadWizardService,
        private jobAdapterService: JobPcvizService,
        private selectionService: ObjectBrowserSelectionService,
        private selectExistingService: SelectExistingLayerService
    ) {}

    ngOnInit(): void {
        this.uploadWizardService.stepChanged.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.updateSummary();
            this.setLayerSummaryNotice();
        });
        this.selectionService.resetSelection();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    createNewLayer(): void {
        this.uploadWizardService.layerSelectionMode = 'new';
    }

    selectLayer(): void {
        this.uploadWizardService.layerSelectionMode = 'existing';
    }

    noneSelected(): boolean {
        return this.uploadWizardService.layerSelectionMode === 'none';
    }

    newSelected(): boolean {
        return this.uploadWizardService.layerSelectionMode === 'new';
    }

    existingSelected(): boolean {
        return this.uploadWizardService.layerSelectionMode === 'existing';
    }

    resetLayerSelectionMode(): void {
        this.uploadWizardService.layerSelectionMode = 'none';
    }

    private updateSummary(): void {
        this.uploadWizardService.summary.splice(1);
        const isNewLayer = this.selectionService.getNumberSelectedElements() === 0;

        if (isNewLayer) {
            this.updateNewLayerSummary();
        } else {
            this.updateExistingLayerSummary();
        }
    }

    private updateExistingLayerSummary(): void {
        const selectedLayer = this.selectionService.getSelectedElements('layers')?.[0];
        this.uploadWizardService.layerName = selectedLayer?.name ?? '';
        this.uploadWizardService.layerDescription = selectedLayer?.description ?? '';

        this.uploadWizardService.summary[0] = {
            title: 'WIZARD.TAB_OUTPUT_LAYER',
            notice: this.selectExistingService.getLayerTypeText(),
            fields: [
                { key: 'UPLOAD.FIELD_NAME', value: this.uploadWizardService.layerName },
                { key: 'UPLOAD.FIELD_DESCRIPTION', value: this.uploadWizardService.layerDescription },
            ],
        };
    }

    private updateNewLayerSummary(): void {
        const layerForm: FormGroup = this.uploadWizardService.layerForm;
        this.uploadWizardService.layerName = layerForm.controls['name'].value;
        this.uploadWizardService.layerDescription = layerForm.controls['description'].value;

        this.uploadWizardService.summary[0] = {
            title: 'WIZARD.TAB_OUTPUT_LAYER',
            notice: 'LAYER_SELECT.CREATE_LAYER_NOTICE',
            fields: [
                { key: 'UPLOAD.FIELD_NAME', value: this.uploadWizardService.layerName },
                { key: 'UPLOAD.FIELD_DESCRIPTION', value: layerForm.controls['description'].value },
            ],
        };

        if (layerForm.controls['hasProject'].value) {
            this.uploadWizardService.summary[1] = {
                title: 'UPLOAD.CREATE_PROJECT',
                notice: 'UPLOAD.CREATE_PROJECT_NOTICE',
                fields: [
                    { key: 'CREATE_PROJECT.NAME', value: layerForm.controls['projectName'].value },
                    { key: 'CREATE_PROJECT.DESCRIPTION', value: layerForm.controls['projectDescription'].value },
                    { key: 'CREATE_PROJECT.CAPTURE_DATE', value: this.uploadWizardService.getFormattedCaptureDate(layerForm, 'longDate') },
                ],
            };
        }
    }

    private setLayerSummaryNotice(): void {
        this.uploadWizardService.layerNoticeSummary = '';
        const layer: string = this.newSelected() ? '_CREATE_LAYER_NOTICE' : '_SELECTED_LAYER_NOTICE';
        const layerType = this.jobAdapterService.getJobAdapter()?.getLayerType();
        if (!layerType) {
            return;
        }

        this.uploadWizardService.layerNoticeSummary = `WIZARD.${layerType}${layer}`;
    }

    get layerType(): string {
        return this.jobAdapterService.getJobAdapter()?.getLayerType() || '';
    }
}
